import {Component, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MenuButtonsHeaderComponent} from '../../shared/components/menu-buttons-header/menu-buttons-header.component';
import {UnlockSliderComponent} from '../../shared/components/unlock-slider/unlock-slider.component';
import {
  SelectableMenuButtonsComponent
} from '../../shared/components/selectable-menu-buttons/selectable-menu-buttons.component';
import {DoorsService} from '../../core/api/doors.service';
import {DoorCommand, DoorCommands, getMessage} from '../../shared/models/door.model';
import {ReservationService} from '../../core/api/reservation.service';
import {ApartmentService} from '../../core/api/apartment.service';
import {forkJoin, of, switchMap} from 'rxjs';
import {
  Apartment,
  buildDoorbellMessageBefore,
  buildDoorbellMessageDuring, hasOpenDoor
} from '../../shared/models/apartment.model';
import {
  availableDate,
  blockedDoor,
  expiredReservation,
  futureReservation,
  requiresDeposit
} from '../../shared/models/reservation.model';
import {
  checkInRequiredAndNoOneCheckedIn,
  checkInRequiredAndNotEveryoneCheckedIn, checkinRequiredForOpen
} from '../../shared/models/check-ins.model';
import {
  ModalCheckInHostsCountComponent
} from '../../shared/components/modal-check-in-hosts-count/modal-check-in-hosts-count.component';
import {
  ModalCheckInLegalAdviceComponent
} from '../../shared/components/modal-check-in-legal-advice/modal-check-in-legal-advice.component';
import {ModalErrorService} from '../../core/services/modal-error.service';
import {ModalInfoService} from '../../core/services/modal-info.service';
import {MetricsService} from '../../core/services/metrics.service';
import {GlobalErrorService} from '../../core/services/global-error.service';
import {CheckInFlowService} from '../../core/services/check-in-flow.service';
import {pendingCheckIns} from '../../shared/models/check-in-flow.model';

export interface TopAction {
  label: string;
  href: string;
  action?: string;
}

@Component({
  selector: 'hv-open',
  imports: [CommonModule, FormsModule, SelectableMenuButtonsComponent, MenuButtonsHeaderComponent, UnlockSliderComponent, ModalCheckInHostsCountComponent, ModalCheckInLegalAdviceComponent],
  templateUrl: './open.component.html',
  standalone: true,
  styleUrls: ['./open.component.scss']
})
export class OpenComponent implements OnInit {

  @ViewChild('doorButtons')
  doorButtons?: SelectableMenuButtonsComponent;

  openFunctionalityIsAvailable = false;
  showCheckinLegalAdviceModal = false;
  showCheckinPendingHostModal = false;
  apartment!: Apartment;
  doorbellMessage = '';
  availableDate = '';
  selectedDoor: string | null = null;
  pendingHostsCounts: number = 0;


  topActions: TopAction[] = [];

  constructor(private doorsService: DoorsService,
              private reservationService: ReservationService,
              private apartmentService: ApartmentService,
              private modalInfoService: ModalInfoService,
              private modalErrorService: ModalErrorService,
              private metricsService: MetricsService,
              private globalErrorService: GlobalErrorService,
              private checkInFlowService: CheckInFlowService,
  ) {
  }

  ngOnInit(): void {

    this.reservationService.getReservation().pipe(
      switchMap((reservation) =>
        forkJoin({
          flow: this.checkInFlowService.get(reservation),
          reservation: of(reservation),
          apartment: this.apartmentService.getApartment(),
        })
      )
    ).subscribe(({flow, reservation, apartment}) => {
      if (flow.hasPendingDeposit) {
        this.showCheckinLegalAdviceModal = true;
        return;
      }
      if (!hasOpenDoor(apartment)) {
        this.enqueueApartmentWithoutOptionModal();
        return;
      }
      if (blockedDoor(reservation)) {
        this.enqueueBlockedModal();
        return;
      }
      if (expiredReservation(reservation)) {
        this.enqueueExpiredModal();
        return;
      }
      if (futureReservation(reservation)) {
        this.availableDate = availableDate(reservation);
        this.enqueueStillNotAvailableModal();
        return;
      }
      if (checkinRequiredForOpen(reservation) && checkInRequiredAndNoOneCheckedIn(flow, reservation)) {
        this.showCheckinLegalAdviceModal = true;
        return;
      }
      if (checkinRequiredForOpen(reservation) && checkInRequiredAndNotEveryoneCheckedIn(flow, reservation)) {
        this.pendingHostsCounts = pendingCheckIns(flow);
        this.showCheckinPendingHostModal = true;
        return;
      }

      this.apartment = apartment;
      this.openFunctionalityIsAvailable = true;
      this.loadDoors();
    });

  }

  public loadDoors() {
    this.doorsService.getDoorCommands().subscribe({
      next: (doorCommands: DoorCommands
      ) => {
        this.topActions = doorCommands.map((doorCommand: DoorCommand) => ({
          action: doorCommand.command,
          label: getMessage(doorCommand),
          href: '',
        }));
      },
      error: (error) => {
        console.error('Error al obtener las puertas:', error);
      }
    });
  }

  onMenuButtonClick(event: any) {
    this.selectedDoor = event.action;
    if (this.selectedDoor == 'open_portal') {
      if (this.apartment.data.push_portal_first == 1) {
        this.doorbellMessage = buildDoorbellMessageBefore(this.apartment);
        this.showDoorbellInfoModal();
      }
    }
  }

  onUnlock() {
    if (!this.selectedDoor) {
      console.error('No se ha seleccionado ninguna puerta');
      this.showSelectDoorModal();
      return;
    }
    if (this.apartment.data.intercom == 1) {
      this.doorbellMessage = buildDoorbellMessageDuring(this.apartment);
      this.showDoorbellInfoModal();
    }
    console.log('Opening door:', this.selectedDoor);
    this.doorsService.openDoor(this.selectedDoor).subscribe({
      next: (response) => {
        this.metricsService.openDoor();
        console.log('Puerta abierta exitosamente:', response);
      },
      error: (error) => {
        this.globalErrorService.markHandled(error);
        console.error('Error al abrir la puerta:', error);
        this.enqueueBlockedModal();
      }
    });
    this.clearSelection();
  }

  clearSelection() {
    this.doorButtons?.clearSelection();
    this.selectedDoor = null;
  }

  closeCheckinLegalAdviceModal() {
    this.showCheckinLegalAdviceModal = false;
  }

  closeCheckinPendingHostModal() {
    this.showCheckinPendingHostModal = false;
  }

  private enqueueBlockedModal(): void {
    this.modalErrorService.enqueue(
      'Acceso bloqueado',
      [
        'Por favor, contacta con tu anfitrión',
      ]
    );
  }

  private showSelectDoorModal(): void {
    this.modalInfoService.open(
      '',
      'Selecciona qué puerta quieres abrir',
    )
  }

  private showDoorbellInfoModal(): void {
    this.modalInfoService.open(
      '',
      this.doorbellMessage,
    )
  }

  private enqueueExpiredModal() {
    this.modalErrorService.enqueue(
      'Tu llave ya no está disponible',
      [
        'Tu reserva ha finalizado'
      ]
    )
  }

  private enqueueStillNotAvailableModal() {
    this.modalErrorService.enqueue(
      'Tu llave aún no está disponible',
      [
        'Podrás abrir a partir del:',
        this.availableDate,
        '¡Gracias!'
      ]
    )
  }

  private enqueueApartmentWithoutOptionModal() {
    this.modalErrorService.enqueue(
      'Opción no disponible',
      [
        'Este apartamento no tiene esta opción',
      ]
    )
  }
}
